/**
 * Students tab — the spec's primary surface.
 *
 * Layout:
 *   [ClassroomSignalHero]
 *   [MetricsRow — 5 cards]
 *   [Segmented student grid:
 *      High performers     (alphabetical)
 *      Stable / Mid
 *      Needs attention
 *      Low engagement]
 *
 * Empty states per plan §4.7.
 *
 * Drill-down opens via ?student=<id> query param (Phase E.3 will render it).
 */

import { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import ClassroomSignalHero from '../../components/teacher-v2/ClassroomSignalHero';
import MetricsRow from '../../components/teacher-v2/MetricsRow';
import StudentCard from '../../components/teacher-v2/StudentCard';
import StudentDrillPanel from '../../components/teacher-v2/StudentDrillPanel';

const SEGMENT_DEFINITIONS = [
  { key: 'high_performers', title: 'High performers',         hint: 'active ≥5 days · accuracy ≥65%' },
  { key: 'stable_mid',      title: 'Stable · mid performers', hint: 'active 3–4 days · accuracy 50–64%' },
  { key: 'needs_attention', title: 'Needs attention',         hint: 'active ≥3 days · accuracy <50%' },
  { key: 'low_engagement',  title: 'Low engagement',          hint: 'active ≤2 days last 7' },
];

function SegmentSection({ title, hint, students, onOpenStudent }) {
  return (
    <section className="mb-6">
      <div className="flex items-center gap-2.5 mb-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-teacher-text-primary">
          {title}
        </h2>
        <span className="text-[11px] text-teacher-text-tertiary">
          {students.length} {students.length === 1 ? 'student' : 'students'} · {hint}
        </span>
        <div className="flex-1 h-px bg-teacher-border" />
      </div>
      {students.length === 0 ? (
        <div className="text-xs text-teacher-text-tertiary py-2">
          No students in this segment.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {students.map(s => (
            <StudentCard key={s.student_id} student={s} onOpen={onOpenStudent} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function ContextStudentsTab() {
  const { batchId, subject } = useOutletContext();
  const [, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getTeacherDashboardV2(batchId, subject)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [batchId, subject]);

  const handleOpenStudent = (studentId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('student', studentId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-sm text-teacher-text-secondary">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-teacher-red-light border border-teacher-red text-teacher-red rounded-lg p-3 text-sm">
          Error: {error}
        </div>
      </div>
    );
  }

  const ctx = data.context;
  const totalStudents = ctx?.total_students || 0;

  // Empty state per plan §4.7 — batch with 0 students assigned.
  if (totalStudents === 0) {
    return (
      <div className="p-6">
        <div className="bg-white border border-teacher-border rounded-xl p-8 text-center">
          <div className="text-base font-medium text-teacher-text-primary mb-2">
            No students in this batch yet
          </div>
          <div className="text-sm text-teacher-text-secondary">
            Add students from the admin portal.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <ClassroomSignalHero
        signal={data.signal}
        lastComputedAt={ctx?.signal_last_computed_at}
      />
      <MetricsRow metrics={data.metrics} totalStudents={totalStudents} />

      <div>
        {SEGMENT_DEFINITIONS.map(seg => (
          <SegmentSection
            key={seg.key}
            title={seg.title}
            hint={seg.hint}
            students={data.segments?.[seg.key] || []}
            onOpenStudent={handleOpenStudent}
          />
        ))}
      </div>

      {/* Slide-in drill panel — controlled by ?student=<id> query param */}
      <StudentDrillPanel batchId={batchId} subject={subject} />
    </div>
  );
}
